import * as L from 'leaflet';

/**
 * Renders a futuristic HUD-style FOV ring for a single fire camera using an L.SVGOverlay.
 *
 * The ring is divided into 10 equal 36° segments. Each segment shows how many
 * seconds ago it was last updated (cyan = fresh <10s, amber = aging <25s, crimson = stale).
 * An animated scanner bracket cycles through segments every 30 s to simulate radar sweep.
 *
 * Call updateSegment(index) from a SignalR handler to mark a segment as freshly scanned.
 *
 * SCALING: OVERLAY_HALF_SIZE controls the on-map size in degrees. ~0.001° ≈ 110m at
 * mid-latitudes. Increase if cameras are far apart; decrease if they overlap.
 */
export class CameraFovOverlay {

  /** Tune this value to change how large the overlay appears on the map. */
  static OVERLAY_HALF_SIZE = 0.02;

  private static _counter = 0;
  private readonly instanceId: number;

  private overlay: L.SVGOverlay;
  private svgElement: SVGSVGElement;

  private segmentUpdateTimes: number[];
  private lastScanningSegment = -1;
  private readonly systemStartTime: number;

  private intervalId: any = null;
  private destroyed = false;
  private clickListener: ((e: MouseEvent) => void) | null = null;

  constructor(
    private readonly map: L.Map,
    private readonly lat: number,
    private readonly lng: number,
    public readonly cameraName: string,
    private readonly segmentCount: number = 12,
    /** Seconds before a segment transitions from cyan → amber. */
    private readonly coldThreshold: number = 60,
    /** Seconds before a segment transitions from amber → crimson. */
    private readonly overdueThreshold: number = 300,
    /** Optional callback invoked when the user clicks a segment. */
    private readonly onSegmentClick?: (segmentIndex: number) => void
  ) {
    this.instanceId = ++CameraFovOverlay._counter;
    this.systemStartTime = Date.now();

    // Initialise all segments as never-seen (will show crimson until real MQTT data arrives)
    this.segmentUpdateTimes = Array.from({ length: this.segmentCount }, () => 0);

    this.svgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg') as SVGSVGElement;
    this.svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    this.svgElement.setAttribute('viewBox', '0 0 220 220');

    const hs = CameraFovOverlay.OVERLAY_HALF_SIZE;
    const bounds: L.LatLngBoundsExpression = [
      [lat - hs, lng - hs],
      [lat + hs, lng + hs]
    ];

    this.overlay = L.svgOverlay(this.svgElement, bounds, {
      interactive: true,
      className: 'camera-fov-overlay'
    });
    this.overlay.addTo(map);

    if (this.onSegmentClick) {
      this.clickListener = (e: MouseEvent) => {
        const seg = (e.target as SVGElement)?.dataset?.['seg'];
        if (seg != null) { this.onSegmentClick(parseInt(seg, 10)); }
      };
      this.svgElement.addEventListener('click', this.clickListener);
    }

    this.startAnimation();
    this.render();
  }

  /**
   * Mark segment [0–9] as freshly scanned.
   * Segment 0 = North (top), increments clockwise.
   * Call this from a SignalR handler when the camera reports activity.
   *
   * @param segmentIndex  0–9
   * @param timestamp     Optional epoch ms; defaults to Date.now()
   */
  updateSegment(segmentIndex: number, timestamp = Date.now()): void {
    if (segmentIndex >= 0 && segmentIndex < this.segmentCount) {
      this.segmentUpdateTimes[segmentIndex] = timestamp;
      this.lastScanningSegment = (segmentIndex + 1) % this.segmentCount;
      this.render();
    }
  }

  /** Remove the overlay from the map and stop all animation loops. */
  remove(): void {
    this.destroyed = true;
    if (this.intervalId !== null) { clearInterval(this.intervalId); this.intervalId = null; }
    if (this.clickListener) { this.svgElement.removeEventListener('click', this.clickListener); this.clickListener = null; }
    try { this.map.removeLayer(this.overlay); } catch { /* already removed */ }
  }

  // ---------------------------------------------------------------------------
  // Private

  private startAnimation(): void {
    // Bracket position is driven purely by updateSegment() (real MQTT data).
    // Only a 1 Hz tick is needed to keep age-counter labels fresh.
    this.intervalId = setInterval(() => {
      if (!this.destroyed) { this.render(); }
    }, 1000);
  }

  private render(): void {
    const el = this.overlay.getElement();
    if (el) { el.innerHTML = this.buildSVG(); }
  }

  private buildSVG(): string {
    const size = 220, c = size / 2, now = Date.now();
    const id = this.instanceId;

    let svg = `<defs>
      <filter id="ng${id}" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="2" result="b"/>
        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
    </defs>`;

    const deg = 360 / this.segmentCount;
    // Offset by half a segment so each segment is centred on its bearing
    const halfDeg = deg / 2;

    // Animated scanning bracket (outer ring highlight)
    if (this.lastScanningSegment !== -1) {
      const ss = this.lastScanningSegment * deg - halfDeg;
      const se = ss + deg - 2;
      svg += `<path d="${this.arc(c, c, 100, 105, ss, se)}" fill="#00f3ff" filter="url(#ng${id})" opacity="0.85"/>`;
    }

    // FOV segments
    for (let i = 0; i < this.segmentCount; i++) {
      const sa = i * deg - halfDeg, ea = sa + deg - 2;
      const never = this.segmentUpdateTimes[i] === 0;
      const secs = never ? Infinity : Math.floor((now - this.segmentUpdateTimes[i]) / 1000);
      const col = this.color(secs);
      svg += `<path data-seg="${i}" d="${this.arc(c, c, 48, 95, sa, ea)}" fill="${col.fill}" stroke="${col.neon}" stroke-width="1.5" style="cursor:${this.onSegmentClick ? 'pointer' : 'default'}"/>`;
      const tp = this.polar(c, c, 72, sa + deg / 2);
      const label = never ? '-' : secs < 3600 ? `${Math.min(secs, 999)}s` : `${Math.floor(secs / 60)}m`;
      svg += `<text x="${tp.x.toFixed(1)}" y="${tp.y.toFixed(1)}" font-size="13" text-anchor="middle" dominant-baseline="central" fill="${col.neon}" font-family="monospace" style="user-select:none">${label}</text>`;
    }

    // Central reticle
    svg += `<circle cx="${c}" cy="${c}" r="38" fill="#030712" stroke="#1f2937" stroke-width="2"/>`;
    svg += `<path d="M${c} ${c-35}L${c} ${c-20}M${c} ${c+35}L${c} ${c+20}M${c-35} ${c}L${c-20} ${c}M${c+35} ${c}L${c+20} ${c}" stroke="#00f3ff" stroke-width="1.5"/>`;

    // Inner hexagon aperture
    const hex = Array.from({ length: 6 }, (_, i) => {
      const p = this.polar(c, c, 15, i * 60);
      return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
    }).join(' ');
    svg += `<polygon points="${hex}" fill="none" stroke="#ffaa00" stroke-width="1.5"/>`;

    // Glowing core dot
    svg += `<circle cx="${c}" cy="${c}" r="4" fill="#00f3ff" filter="url(#ng${id})"/>`;

    // Camera name
    const label = this.cameraName.length > 18 ? this.cameraName.substring(0, 16) + '\u2026' : this.cameraName;
    svg += `<text x="${c}" y="212" font-size="10" text-anchor="middle" fill="#00f3ff" font-family="monospace" style="user-select:none">${label}</text>`;

    return svg;
  }

  private polar(cx: number, cy: number, r: number, deg: number) {
    const rad = (deg - 90) * Math.PI / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  private arc(cx: number, cy: number, r1: number, r2: number, a1: number, a2: number): string {
    const so = this.polar(cx, cy, r2, a1), eo = this.polar(cx, cy, r2, a2);
    const si = this.polar(cx, cy, r1, a1), ei = this.polar(cx, cy, r1, a2);
    const laf = a2 - a1 <= 180 ? '0' : '1';
    return `M${so.x.toFixed(2)} ${so.y.toFixed(2)} A${r2} ${r2} 0 ${laf} 1 ${eo.x.toFixed(2)} ${eo.y.toFixed(2)} L${ei.x.toFixed(2)} ${ei.y.toFixed(2)} A${r1} ${r1} 0 ${laf} 0 ${si.x.toFixed(2)} ${si.y.toFixed(2)} Z`;
  }

  private color(seconds: number): { neon: string; fill: string } {
    if (seconds < this.coldThreshold) return { neon: '#00f3ff', fill: 'rgba(0,243,255,0.15)' };
    if (seconds < this.overdueThreshold) return { neon: '#ffaa00', fill: 'rgba(255,170,0,0.15)' };
    return { neon: '#ff003c', fill: 'rgba(255,0,60,0.15)' };
  }
}
